import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { t } from '../i18n';
import { useCustomers } from '../lib/queries';
import { useCan } from '../lib/auth';
import { fmtDate, fmtMoney, fmtRelative } from '../lib/format';
import type { CustomerKind, CustomerStatus, PaymentTerms } from '../lib/types';
import { CustomerStatusBadge, Empty, ErrorState, Input, Ltr, Pagination, Select, SkeletonRows, SortTh, Tabs, useDebounced } from '../components/ui';
import { IconPlus, IconSearch, IconUpload } from '../components/icons';

const KIND_HINT: Record<CustomerKind, string> = {
  buyers: 'לקוחות שיש על שמם חשבונית מס, חשבונית מס/קבלה, קבלה או תעודת משלוח (כולל הסניפים שלהם).',
  quote_only: 'קיבלו הצעת מחיר ועדיין לא הזמינו — מתאים למעקב וחזרה אליהם.',
  none: 'לקוחות שעדיין לא הופק עבורם אף מסמך (למשל מייבוא קובץ הלקוחות).',
};

type SortKey ='businessName' | 'companyNumber' | 'lastInvoiceDate' | 'totalInvoiced' | 'outstanding' | 'lastActivityAt';

export function CustomersPage() {
  const can = useCan();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(params.get('q') ?? '');
  const dq = useDebounced(q, 250);

  const status = params.get('status') ?? '';
  const terms = params.get('terms') ?? '';
  const balance = params.get('balance') ?? '';
  const kind = (params.get('kind') ?? '') as CustomerKind | '';
  const sort = (params.get('sort') as SortKey) || 'businessName';
  const dir = (params.get('dir') as 'asc' | 'desc') || 'asc';
  const page = Number(params.get('page')) || 1;

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) (v ? next.set(k, v) : next.delete(k));
    if (!('page' in patch)) next.delete('page');
    setParams(next, { replace: true });
  };

  useEffect(() => {
    if ((params.get('q') ?? '') !== dq) update({ q: dq || null });
  }, [dq]);

  const { data, isLoading, isFetching, error, refetch } = useCustomers({
    q: dq || undefined,
    status: status || undefined,
    paymentTerms: terms || undefined,
    balance: balance || undefined,
    kind: kind || undefined,
    sort,
    dir,
    page,
    pageSize: 50,
  });

  const onSort = (k: SortKey) => update({ sort: k, dir: sort === k && dir === 'asc' ? 'desc' : k === 'businessName' || k === 'companyNumber' ? 'asc' : 'desc' });
  const filtered = Boolean(dq || status || terms || balance || kind);
  const kc = data?.kindCounts;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.customers.title}</h1>
          <div className="sub">{data ? `${data.total} ${t.common.results}` : ' '}</div>
        </div>
        {can('customers.edit') ? (
          <div className="row">
            <Link to="/customers/import" className="btn">
              <IconUpload /> {t.customers.import}
            </Link>
            <Link to="/customers/new" className="btn primary">
              <IconPlus /> {t.customers.new}
            </Link>
          </div>
        ) : null}
      </div>

      <Tabs<CustomerKind | ''>
        value={kind}
        onChange={(k) => update({ kind: k || null })}
        tabs={[
          { key: '', label: 'כל הלקוחות', count: kc?.all },
          { key: 'buyers', label: 'לקוחות שהזמינו', count: kc?.buyers },
          { key: 'quote_only', label: 'הצעת מחיר בלבד', count: kc?.quote_only },
          { key: 'none', label: 'ללא מסמכים', count: kc?.none },
        ]}
      />
      {kind ? <p className="muted small" style={{ margin: '-4px 0 12px' }}>{KIND_HINT[kind]}</p> : null}

      <div className="panel">
        <div className="panel-head row wrap" style={{ gap: 8 }}>
          <div className="search-input" style={{ flex: '1 1 260px', maxWidth: 380 }}>
            <IconSearch />
            <Input placeholder={t.customers.searchPlaceholder} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t.common.search} />
          </div>
          <Select style={{ width: 150 }} value={status} onChange={(e) => update({ status: e.target.value || null })} aria-label={t.customers.filters.status}>
            <option value="">{t.customers.filters.status}: {t.common.all}</option>
            {(Object.keys(t.enums.customerStatus) as CustomerStatus[]).map((s) => (
              <option key={s} value={s}>{t.enums.customerStatus[s]}</option>
            ))}
          </Select>
          <Select style={{ width: 170 }} value={terms} onChange={(e) => update({ terms: e.target.value || null })} aria-label={t.customers.filters.terms}>
            <option value="">{t.customers.filters.terms}: {t.common.all}</option>
            {(Object.keys(t.enums.paymentTerms) as PaymentTerms[]).map((s) => (
              <option key={s} value={s}>{t.enums.paymentTerms[s]}</option>
            ))}
          </Select>
          <Select style={{ width: 150 }} value={balance} onChange={(e) => update({ balance: e.target.value || null })} aria-label={t.customers.filters.balance}>
            <option value="">{t.customers.filters.balance}: {t.common.all}</option>
            <option value="outstanding">{t.customers.filters.outstanding}</option>
            <option value="overdue">{t.customers.filters.overdue}</option>
          </Select>
          {isFetching && !isLoading ? <span className="spinner" style={{ color: 'var(--faint)' }} /> : null}
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : data && data.items.length === 0 ? (
          filtered ? (
            <Empty title={t.customers.noResults.title} body={t.customers.noResults.body} />
          ) : (
            <Empty
              title={t.customers.empty.title}
              body={t.customers.empty.body}
              actions={
                <>
                  <Link to="/customers/new" className="btn primary">{t.customers.new}</Link>
                  <Link to="/customers/import" className="btn">{t.customers.import}</Link>
                </>
              }
            />
          )
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <SortTh label={t.customers.cols.name} sortKey="businessName" sort={sort} dir={dir} onSort={onSort} />
                    <SortTh label={t.customers.cols.companyNumber} sortKey="companyNumber" sort={sort} dir={dir} onSort={onSort} className="hide-sm" />
                    <th className="hide-sm">{t.customers.cols.contact}</th>
                    <th className="hide-sm">{t.customers.cols.terms}</th>
                    <SortTh label={t.customers.cols.lastInvoice} sortKey="lastInvoiceDate" sort={sort} dir={dir} onSort={onSort} className="hide-sm" />
                    <SortTh label={t.customers.cols.totalInvoiced} sortKey="totalInvoiced" sort={sort} dir={dir} onSort={onSort} className="num" />
                    <SortTh label={t.customers.cols.outstanding} sortKey="outstanding" sort={sort} dir={dir} onSort={onSort} className="num" />
                    <th>{t.customers.cols.status}</th>
                    <SortTh label={t.customers.cols.lastActivity} sortKey="lastActivityAt" sort={sort} dir={dir} onSort={onSort} className="hide-sm" />
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <SkeletonRows cols={9} rows={10} />
                  ) : (
                    data?.items.map((c) => (
                      <tr
                        key={c.id}
                        className="clickable"
                        onClick={(e) => (e.metaKey || e.ctrlKey ? window.open(`/customers/${c.id}`, '_blank') : navigate(`/customers/${c.id}`))}
                      >
                        <td className="primary" style={{ maxWidth: 320 }}>
                          <Link to={`/customers/${c.id}`} onClick={(e) => e.stopPropagation()} style={{ color: 'var(--ink)' }} className="ellipsis" title={c.businessName}>
                            {c.businessName}
                          </Link>
                          {c.parentName ? <span className="sub">{t.customers.branchOf} {c.parentName}</span> : null}
                          {c.branchCount ? <span className="sub">{c.branchCount} ענפים / סניפים</span> : null}
                          {!kind && c.kind === 'quote_only' ? <span className="badge warn" style={{ marginInlineStart: 6 }}>הצעת מחיר בלבד</span> : null}
                        </td>
                        <td className="hide-sm tabular">{c.companyNumber ? <Ltr>{c.companyNumber}</Ltr> : <span className="faint">—</span>}</td>
                        <td className="hide-sm">
                          {c.contactName ?? <span className="faint">—</span>}
                          {c.phone ? <span className="sub"><Ltr>{c.phone}</Ltr></span> : null}
                        </td>
                        <td className="hide-sm nowrap">{c.paymentTermsLabel}</td>
                        <td className="hide-sm tabular">{fmtDate(c.lastInvoiceDate)}</td>
                        <td className="num">{c.totalInvoiced ? fmtMoney(c.totalInvoiced) : <span className="faint">—</span>}</td>
                        <td className="num" style={(c.overdue ?? 0) > 0 ? { color: 'var(--bad)' } : undefined}>
                          {c.outstanding ? fmtMoney(c.outstanding) : <span className="faint">—</span>}
                        </td>
                        <td><CustomerStatusBadge status={c.status} /></td>
                        <td className="hide-sm muted small nowrap">{fmtRelative(c.lastActivityAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {data ? <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={(p) => update({ page: String(p) })} /> : null}
          </>
        )}
      </div>
    </>
  );
}
